import React, { useMemo, useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  Add,
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { format, isValid } from "date-fns";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { StatusChip } from "../../common/StatusChip";
import { RowActions } from "../../common/RowActions";
import { useGetBoardMembersQuery } from "../../../services/appointees.query";
import { ExportLoadingBackdrop } from "../../common/ExportLoadingBackdrop";
import { useTableState } from "../../../hooks/useTableState";
import { Notify } from "../../../layouts/notify";

type BoardMemberRow = {
  id: number;
  first_name: string;
  middle_initial: string;
  last_name: string;
  profile_url: string;
  appointment_date: string;
  description: string;
  is_active: boolean;
};

export default function BoardMembers() {
  const navigate = useNavigate();

  const defaultFilters = useMemo(
    () => ({
      search: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
  });

  const [draftSearch, setDraftSearch] = useState(filters.search);

  useEffect(() => {
    setDraftSearch(filters.search);
  }, [filters]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const {
    data: boardMembersData = [],
    isLoading,
    isFetching,
    isError,
  } = useGetBoardMembersQuery("");

  const editBoardMember = (rowData: BoardMemberRow) => {
    navigate("/app-contents/board-member-details", {
      state: { boardMember: rowData },
    });
  };

  const handleSearch = () => {
    setFilters({
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredData = useMemo(() => {
    if (!filters.search.trim()) return boardMembersData;
    const query = filters.search.toLowerCase();
    return boardMembersData.filter((member: BoardMemberRow) => {
      const fullName =
        `${member.first_name || ""} ${member.middle_initial || ""} ${member.last_name || ""}`.toLowerCase();
      return fullName.includes(query);
    });
  }, [filters.search, boardMembersData]);

  const totalCount = filteredData.length;
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Preparing PDF document for all ${filteredData.length.toLocaleString()} board members...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const doc = new jsPDF("portrait", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Board Members Directory", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Members: ${filteredData.length}`,
        14,
        22
      );

      const tableColumn = ["#", "Board Member Name", "Appointment Date", "Status"];
      const tableRows = filteredData.map((item: BoardMemberRow, idx: number) => {
        const fullName = `${item.last_name || ""}, ${item.first_name || ""} ${item.middle_initial || ""}`.trim();
        const dateStr = item.appointment_date && isValid(new Date(item.appointment_date))
          ? format(new Date(item.appointment_date), "MMMM dd, yyyy")
          : "—";

        return [
          idx + 1,
          fullName || "—",
          dateStr,
          item.is_active ? "ACTIVE" : "INACTIVE",
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8.5,
          cellPadding: 3,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Board_Members_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${filteredData.length} board members to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for all ${filteredData.length.toLocaleString()} board members...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const rows = filteredData.map((item: BoardMemberRow, idx: number) => ({
        "No.": idx + 1,
        "Last Name": item.last_name || "—",
        "First Name": item.first_name || "—",
        "Middle Initial": item.middle_initial || "—",
        "Appointment Date": item.appointment_date && isValid(new Date(item.appointment_date))
          ? format(new Date(item.appointment_date), "yyyy-MM-dd")
          : "—",
        "Status": item.is_active ? "ACTIVE" : "INACTIVE",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Board Members");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 20 },
        { wch: 20 },
        { wch: 14 },
        { wch: 20 },
        { wch: 14 },
      ];

      XLSX.writeFile(wb, `MTRCB_Board_Members_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${filteredData.length} board members to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "number",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography>{(page - 1) * pageSize + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "profile_url",
        header: "Member",
        size: 320,
        Cell: ({ row }: any) => {
          const member = row.original;
          const fullName = `${member.first_name} ${member.middle_initial || ""} ${member.last_name}`;

          return (
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar
                src={member.profile_url}
                alt={fullName}
                sx={{
                  width: 38,
                  height: 38,
                  cursor: member.profile_url ? "pointer" : "default",
                }}
                onClick={() => {
                  if (member.profile_url) {
                    window.open(member.profile_url, "_blank");
                  }
                }}
              >
                {member.first_name?.[0]}
              </Avatar>
              <Typography fontWeight={600} color="#1E293B">
                {fullName}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "appointment_date",
        header: "Appointment Date",
        size: 200,
        Cell: ({ cell }: any) => {
          const val = cell.getValue();
          if (!val) return <Typography>—</Typography>;
          const parsedDate = new Date(val);
          return (
            <Typography sx={{ fontSize: 13.5, color: "#475569" }}>
              {isValid(parsedDate) ? format(parsedDate, "MMMM dd, yyyy") : "—"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "is_active",
        header: "Status",
        size: 130,
        Cell: ({ row }: any) => {
          const isActive = row.original.is_active;
          return (
            <StatusChip
              label={isActive ? "ACTIVE" : "INACTIVE"}
              tone={isActive ? "success" : "neutral"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "Edit",
                onClick: () => editBoardMember(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage
      title="Board Members"
      subTitle="Manage board member profiles and appointments."
      primaryButtonProps={{
        startIcon: <Add />,
        children: "Add New Board Member",
        onClick: () => {
          navigate("/app-contents/board-member-details");
        },
      }}
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered board members. Set filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load board members.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Search Board Members"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive<BoardMemberRow>
        data={paginatedData}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRowClick={(row: any) => editBoardMember(row.original)}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />
    </DefaultPage>
  );
}
